import { NotificationList } from 'components/common/NotificationList'
import { put, takeLatest, call } from 'redux-saga/effects'
import {
  getRequest,
  makePutRequest,
  makePostRequest,
  makeDeleteRequest,
  makeLeadsPostRequest
} from 'utils/api'
import { formatResponse, formatErrorMessage } from 'utils/formatting'
import { toContactData, toContactDeals, toContactEmail } from './serializers'
import { toContactFormData } from 'components/Contacts/serializers'

import * as ACT from './actions'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'

function* fetchContact(action) {
  try {
    const { id } = action.payload
    const url = `/v1/contacts/${id}/details`
    const contact = yield call(getRequest, url)
    yield put({
      type: ACT.CONTACT_FETCH_SUCCESS,
      payload: toContactData(contact)
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Contact',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchCompanyDeals(action) {
  try {
    const { companyId } = action.payload
    const url = `/v1/deals/company/${companyId}/active`
    const companyDeals = yield call(getRequest, url)

    yield put({
      type: ACT.ACTIVE_COMPANY_DEALS_FETCH_SUCCESS,
      payload: {
        companyDeals: toContactDeals(companyDeals)
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Company Opportunities',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* removeDealContact(action) {
  try {
    const { dealId, contactId } = action.payload
    const url = `/v1/deals/${dealId}/removecontact/${contactId}`
    yield call(makePutRequest, url)
    yield put({
      type: ACT.REMOVE_DEAL_CONTACT_SUCCESS,
      payload: {
        dealId
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue removing the Contact from the Opportunity',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* updateContact(action) {
  const { id, contact, fileInfo } = action.payload
  try {
    const url = `/v1/contacts/${id}/form`
    const response = yield makePutRequest(url, toContactFormData(contact, fileInfo))

    yield put({
      type: ACT.CONTACT_UPDATE_SUCCESS,
      payload: {
        contact: response
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Contact updated successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Contact',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* deleteContact(action) {
  try {
    const { id, navigate } = action.payload
    const url = `/v1/contacts/${id}`
    yield makeDeleteRequest(url)

    yield put({
      type: ACT.CONTACT_DELETE_SUCCESS
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Contact deleted successfully',
        level: 'success'
      }
    })
    navigate('/contacts')
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue deleting the contact',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* emailContact(action) {
  const {
    fromUser,
    toContact,
    ccContacts,
    bccContacts,
    subject,
    emailBody,
    files,
    fromContactsList,
    withSaveEmailAsNote,
    entityId,
    splitMode,
    app
  } = action.payload
  try {
    const url = '/v1/email/send'
    const emailData = toContactEmail(
      fromUser,
      toContact,
      ccContacts,
      bccContacts,
      subject,
      emailBody,
      files,
      fromContactsList,
      withSaveEmailAsNote,
      entityId,
      splitMode,
      app
    )

    if (app === 'leads') {
      yield makeLeadsPostRequest(url, emailData)
    } else {
      yield makePostRequest(url, emailData)
    }

    yield put({ type: ACT.EMAIL_CONTACT_SUCCESS })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Contact email sent successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue emailing the Contact',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchContactNotes(action) {
  try {
    const { id } = action.payload
    const url = `/v1/notes/contact/${id}`
    const notes = yield getRequest(url)

    yield put({
      type: ACT.CONTACT_NOTES_FETCH_SUCCESS,
      payload: {
        notes: notes
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Contact Notes',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* addContactNote(action) {
  const { data } = action.payload
  try {
    const url = '/v1/notes'
    const note = yield makePostRequest(url, data)

    yield put({
      type: ACT.CONTACT_ADD_NOTE_SUCCESS,
      payload: {
        note
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Note added successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue adding Note',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* updateContactNote(action) {
  const { data, noteId } = action.payload
  try {
    const url = `/v1/notes/${noteId}`
    const note = yield makePutRequest(url, data)

    yield put({
      type: ACT.CONTACT_UPDATE_NOTE_SUCCESS,
      payload: {
        note,
        noteId
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Note updated successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Note',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* deleteContactNote(action) {
  const { noteId } = action.payload
  try {
    const url = `/v1/notes/${noteId}`
    yield makeDeleteRequest(url)

    yield put({
      type: ACT.CONTACT_DELETE_NOTE_SUCCESS,
      payload: {
        noteId
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Note deleted successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue deleting Note',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

const contactSagas = [
  takeLatest(ACT.CONTACT_FETCH_REQUESTED, fetchContact),
  takeLatest(ACT.ACTIVE_COMPANY_DEALS_FETCH_REQUESTED, fetchCompanyDeals),
  takeLatest(ACT.REMOVE_DEAL_CONTACT_REQUESTED, removeDealContact),
  takeLatest(ACT.CONTACT_UPDATE_REQUESTTED, updateContact),
  takeLatest(ACT.CONTACT_DELETE_REQUESTED, deleteContact),
  takeLatest(ACT.EMAIL_CONTACT_REQUESTED, emailContact),
  takeLatest(ACT.CONTACT_NOTES_FETCH_REQUESTED, fetchContactNotes),
  takeLatest(ACT.CONTACT_ADD_NOTE_REQUESTED, addContactNote),
  takeLatest(ACT.CONTACT_DELETE_NOTE_REQUESTED, deleteContactNote),
  takeLatest(ACT.CONTACT_UPDATE_NOTE_REQUESTED, updateContactNote)
]

export default contactSagas
